import { InfoOutlined, WarningAmberRounded } from "@mui/icons-material";
import TabContext from "@mui/lab/TabContext";
import TabPanel from "@mui/lab/TabPanel";
import { Box, Grid, MenuItem, Stack, Tooltip, Typography, styled } from "@mui/material";
import { center, point } from "@turf/turf";
import { Divider, SeverityIcon, Tab, Tabs, TextField } from "components_new";
import { uniq } from "lodash";
import mapboxgl, { LngLatLike, Map, Marker, Popup } from "mapbox-gl";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

import { MapBoxStyles } from "features/map/baseMapStyles";
import { getBounds } from "features/map/utils";

import { Popup as PopupComponent, getMapHomeControl } from "components";

import { useAppSelector } from "hooks";

import { DataState } from "store/interfaces";

import { themeColors } from "theme/themeConstants";

import {
  DistanceSimplifications,
  DistanceTypes,
  ValidationMessageDetail,
  ValidationMessageSeverity,
  Zoning,
} from "types";

import { AttributesTab } from "./AttributesTab";
import { DEFAULT_INTERVAL_M, DEFAULT_MAX_INTERVAL_M, DEFAULT_SQR_ROOT_AREA_FACTOR } from "./CreateCustomZoning";
import { MapPreview } from "./MapPreview";
import { OptionsTab } from "./OptionsTab";
import { SummaryTab } from "./SummaryTab";
import { ValidationTab } from "./ValidationTab";

import "mapbox-gl/dist/mapbox-gl.css";

/* eslint-disable @typescript-eslint/no-var-requires */
/* eslint-disable import/no-webpack-loader-syntax */
(mapboxgl as any).workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

/* eslint-enable @typescript-eslint/no-var-requires */
/* eslint-enable import/no-webpack-loader-syntax */

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN as string;

enum CreateCustomZoningTab {
  SUMMARY = "summary",
  ATTRIBUTES = "attributes",
  VALIDATION = "validation",
  OPTIONS = "options",
}

interface Settings {
  loading: boolean;
  activeStep: number;
  selectedIdField: string;
  selectedNameField: string;
  zoningName: string;
  zoningDescription: string;
  selectedAttributes: string[];
  selectedSimplification: DistanceSimplifications;
  distanceType: DistanceTypes;
  isSimplificationActive: boolean;
  setZoningName: (name: string) => void;
  setSelectedIdField: (idField: string) => void;
  setSelectedNameField: (nameField: string) => void;
  setZoningDescription: (description: string) => void;
  setSelectedSimplification: (simplification: DistanceSimplifications) => void;
  setSelectedAttributes: (attributes: string[]) => void;
  setDistanceType: (distanceType: DistanceTypes) => void;
  setIsSemplificationActive: (isSimplificationActive: boolean) => void;
}

interface Props {
  interactive: boolean;
  zoning: Zoning;
  settings: Settings;
}

const Wrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  height: 100%;
`;

export const StyledTabPanel = styled(TabPanel)`
  padding: 0;
  max-height: 258px;
  overflow-y: auto;
`;

export const StyledTab = styled(Tab)`
  & > .MuiTab-iconWrapper {
    margin-right: 2px;
  }
`;

const AttributesCount = styled(Box)`
  min-width: 40px;
  margin-left: 5px;
  text-align: left;
`;

export const CustomZoningInfo = ({ interactive, zoning, settings }: Props) => {
  const {
    loading,
    activeStep,
    zoningName,
    zoningDescription,
    selectedIdField,
    selectedNameField,
    selectedAttributes,
    selectedSimplification,
    distanceType,
    isSimplificationActive,
    setZoningName,
    setSelectedIdField,
    setSelectedNameField,
    setZoningDescription,
    setSelectedSimplification,
    setSelectedAttributes,
    setDistanceType,
    setIsSemplificationActive,
  } = settings;

  const [activeTab, setActiveTab] = useState(CreateCustomZoningTab.SUMMARY);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState<ValidationMessageDetail | null>(null);
  const [popupContent, setPopupContent] = useState<ReactNode | null>(null);

  const popup = useRef<Popup | null>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const markers = useRef<Marker[]>([]);
  const mapContainer: any = useRef(null);
  const map = useRef<Map | null>(null);

  const zoningPreparing = useAppSelector((state) => state.datasetFolders.zoningPreparing);

  const validationMessagesWithIds = useMemo(
    () =>
      zoning.validation.messages.map((message) => ({
        ...message,
        details: message.details?.map((detail) => ({ ...detail, id: uuid() })),
      })),

    [zoning.validation.messages],
  );

  useEffect(() => {
    if (map.current || !zoning.zoningId || !zoning.schema.idField) return; // initialize map only once

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MapBoxStyles.Default,
      bounds: getBounds(zoning.boundingBox),
      fitBoundsOptions: {
        padding: 20,
      },
      pitch: 0,
      bearing: 1,
      logoPosition: "bottom-right",
      transformRequest: (url, resourceType): any => {
        if (resourceType === "Tile" && url.startsWith(process.env.REACT_APP_API_HOST as string)) {
          return {
            headers: {
              authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
            },
            url,
          };
        }
      },
    });
    // disable map rotation using right click + drag
    map.current.dragRotate.disable();

    // disable map rotation using touch rotation gesture
    map.current.touchZoomRotate.disableRotation();

    // Add full extent control to the map.
    map.current.addControl(
      getMapHomeControl(map.current, getBounds(zoning.boundingBox), {
        padding: 20,
      }),
      "bottom-left",
    );

    // Add zoom controls to the map.
    map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "bottom-left");

    map.current.on("load", () => {
      setMapLoaded(true);
    });
  }, [zoning.zoningId, zoning.schema.idField, zoning.boundingBox]);

  useEffect(() => {
    if (!loading) {
      setActiveTab(CreateCustomZoningTab.SUMMARY);
    }
  }, [loading]);

  useEffect(() => {
    if (activeStep === 2 && zoningPreparing.state === DataState.AVAILABLE) {
      setSelectedIdField(zoningPreparing.data.zoning.schema.idField);
      setSelectedNameField(zoningPreparing.data.zoning.schema.nameField || "");
    }
  }, [
    setSelectedIdField,
    setSelectedNameField,
    zoningPreparing.data?.zoning.schema.idField,
    zoningPreparing.data?.zoning.schema.nameField,
    zoningPreparing.state,
    activeStep,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: CreateCustomZoningTab) => {
    if (activeTab === CreateCustomZoningTab.VALIDATION) handleDeselectIssue();
    setActiveTab(newValue);
  };

  const handleChangeZoningName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZoningName(event.target.value);
  };

  const handleChangeZoningDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setZoningDescription(event.target.value);
  };

  const handleChangeSimplification = (simplification: Partial<DistanceSimplifications>) => {
    setSelectedSimplification({ ...selectedSimplification, ...simplification });
  };

  const handleToggleAttribute = (attribute: string) => {
    if (selectedAttributes.includes(attribute)) {
      setSelectedAttributes(selectedAttributes.filter((selectedAttribute) => selectedAttribute !== attribute));
    } else {
      setSelectedAttributes([...selectedAttributes, attribute]);
    }
  };

  const handleToggleAllAttributes = () => {
    if (selectedAttributes.length === zoning.schema.attributes.length) {
      const newAttributes = [selectedIdField];

      if (selectedNameField) {
        newAttributes.push(selectedNameField);
      }

      setSelectedAttributes(newAttributes);
    } else {
      setSelectedAttributes(zoning.schema.attributes.map((attribute) => attribute.name));
    }
  };

  const handleChangeDistanceType = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDistanceType = e.target.value as DistanceTypes;
    setDistanceType(newDistanceType);

    if (newDistanceType === DistanceTypes.AreaDependent) {
      handleChangeSimplification({
        sqrRootAreaFactor: DEFAULT_SQR_ROOT_AREA_FACTOR.toString(),
        maxIntervalM: DEFAULT_MAX_INTERVAL_M.toString(),
      });
    } else {
      handleChangeSimplification({
        intervalM: DEFAULT_INTERVAL_M.toString(),
      });
    }
  };

  const toggleSimplification = (isActive: boolean) => {
    setIsSemplificationActive(isActive);
  };

  const removeMarkersAndPopup = () => {
    markers.current?.forEach((marker) => marker.remove());
    markers.current = [];

    popup.current?.remove();
    setPopupContent(null);
  };

  const handleDeselectIssue = useCallback(() => {
    removeMarkersAndPopup();
    setSelectedIssue(null);
  }, []);

  const handleSelectIssue = useCallback((severity: ValidationMessageSeverity, issue: ValidationMessageDetail) => {
    removeMarkersAndPopup();

    if (issue) {
      const features = issue.locations?.map((l) => point([l.lon, l.lat])) || [];
      const issuesCenter = center({
        type: "FeatureCollection",
        features,
      });

      map.current?.flyTo({ center: issuesCenter.geometry.coordinates as any, zoom: 14 });

      const newMarkers: Marker[] = [];

      issue.locations?.forEach((location) => {
        const newMarker = new Marker({
          scale: 0.6,
          color: severity === ValidationMessageSeverity.Warning ? "orange" : "red",
        })
          .setLngLat({ lon: location.lon, lat: location.lat })
          .addTo(map.current as Map);

        newMarkers.push(newMarker);
      });
      markers.current = newMarkers;
      setSelectedIssue(issue);
    }
  }, []);

  const handleOpenPopup = useCallback((newPopup: Popup, lngLat: LngLatLike, popupContent: ReactNode) => {
    if (map.current && popupRef.current) {
      newPopup.setLngLat(lngLat).setDOMContent(popupRef.current).addTo(map.current);
      popup.current = newPopup;
      setPopupContent(popupContent);
    }
  }, []);

  const handleClosePopup = useCallback(() => {
    if (popup.current) {
      popup.current.remove();
      popup.current = null;
      setPopupContent(null);
    }
  }, []);

  return (
    <Wrapper>
      <Stack spacing={2} marginTop={1}>
        <TextField
          fullWidth
          required
          label="Zoning Name"
          value={zoningName}
          size="small"
          onChange={handleChangeZoningName}
          disabled={loading || !interactive}
        />
        <Grid container>
          <Grid item xs={6} container alignItems={"center"} paddingRight={1}>
            <Grid item xs mr={1}>
              <TextField
                required
                fullWidth
                select
                label="Zone ID"
                size="small"
                value={selectedIdField}
                disabled={activeStep === 2 || loading || !interactive}
                onChange={(e) => setSelectedIdField(e.target.value)}
              >
                <MenuItem value={""}></MenuItem>
                {Object.entries(zoning.statistics.attributes)
                  .filter(([attribute, values]) => values?.hasUniqueValues)
                  .map(([attribute], index) => (
                    <MenuItem key={index} value={attribute}>
                      <Grid container alignItems={"center"}>
                        {attribute}
                        {attribute === "__fid" && (
                          <Typography variant="caption" component={"span"} color={themeColors.textSecondary} ml={1}>
                            (auto-generated)
                          </Typography>
                        )}
                      </Grid>
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Tooltip
              title={
                <>
                  The <b>zone id</b> values are used to identify zones in the application, and are included in export
                  files
                </>
              }
            >
              <InfoOutlined
                fontSize="small"
                sx={{
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          </Grid>

          <Grid item xs={6} paddingLeft={1}>
            <TextField
              fullWidth
              select
              label="Display Name"
              size="small"
              value={selectedNameField}
              onChange={(e) => setSelectedNameField(e.target.value)}
              disabled={activeStep === 2 || loading || !interactive}
            >
              <MenuItem value={""}></MenuItem>
              {Object.entries(zoning.statistics.attributes).map(([attribute, values], index) => (
                <MenuItem key={index} value={attribute}>
                  <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                    <Grid container alignItems={"center"}>
                      {attribute}
                      {attribute === "__fid" && (
                        <Typography variant="caption" component={"span"} color={themeColors.textSecondary} ml={1}>
                          (auto-generated)
                        </Typography>
                      )}
                    </Grid>
                    {!values?.hasUniqueValues && (
                      <Tooltip title="This field has non-unique values" color="warning">
                        <WarningAmberRounded />
                      </Tooltip>
                    )}
                  </div>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <TextField
          fullWidth
          multiline
          value={zoningDescription}
          minRows={3}
          maxRows={2}
          placeholder="Description"
          className={interactive ? "" : "disabled"}
          onChange={handleChangeZoningDescription}
          disabled={loading || !interactive}
        />

        <Divider sx={{ fontWeight: 500 }}>{`${activeStep === 2 ? "Prepared" : "Uploaded"} Zoning`}</Divider>
        <Box>
          <TabContext value={activeTab}>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <StyledTab label="Summary" value={CreateCustomZoningTab.SUMMARY} />
              <StyledTab
                label={
                  <>
                    Attributes
                    {activeStep === 1 && (
                      <AttributesCount>{`(${
                        uniq([...selectedAttributes, selectedIdField, selectedNameField].filter((i) => i)).length
                      }/${zoning.schema.attributes.length})`}</AttributesCount>
                    )}
                  </>
                }
                value={CreateCustomZoningTab.ATTRIBUTES}
              />
              <StyledTab
                label="Validation"
                icon={<SeverityIcon severity={zoning.validation.maxSeverity} fontSize="small" />}
                value={CreateCustomZoningTab.VALIDATION}
              />
              {interactive && <StyledTab label="Options" value={CreateCustomZoningTab.OPTIONS} />}
            </Tabs>
            {interactive && (
              <StyledTabPanel value={CreateCustomZoningTab.OPTIONS}>
                <OptionsTab
                  disabled={activeStep === 2 || loading || !interactive}
                  distanceType={distanceType}
                  simplification={selectedSimplification}
                  isSimplificationActive={isSimplificationActive}
                  updateSimplification={handleChangeSimplification}
                  handleChangeDistanceType={handleChangeDistanceType}
                  toggleSimplification={toggleSimplification}
                />
              </StyledTabPanel>
            )}
            <StyledTabPanel value={CreateCustomZoningTab.SUMMARY}>
              <SummaryTab zoning={zoning} />
            </StyledTabPanel>
            <StyledTabPanel value={CreateCustomZoningTab.ATTRIBUTES}>
              <AttributesTab
                idField={selectedIdField}
                nameField={selectedNameField}
                attributesStatistics={zoning.statistics.attributes}
                attributes={zoning.schema.attributes}
                selectedAttributes={selectedAttributes}
                toggleAttribute={handleToggleAttribute}
                toggleAllAttributes={handleToggleAllAttributes}
                disabled={activeStep === 2 || !interactive}
              />
            </StyledTabPanel>
            <StyledTabPanel value={CreateCustomZoningTab.VALIDATION}>
              <ValidationTab
                validationMessages={validationMessagesWithIds}
                selectedIssue={selectedIssue}
                handleSelectIssue={handleSelectIssue}
              />
            </StyledTabPanel>
          </TabContext>
        </Box>
      </Stack>
      <Box>
        <MapPreview
          ref={mapContainer}
          map={map.current}
          mapLoaded={mapLoaded}
          zoningId={zoning.zoningId}
          zoningIdField={zoning.schema.idField}
          validationMessages={validationMessagesWithIds}
          popup={popup.current}
          minZoom={zoning.minZoom}
          maxZoom={zoning.maxZoom}
          handleDeselectIssue={handleDeselectIssue}
          handleOpenPopup={handleOpenPopup}
          handleClosePopup={handleClosePopup}
        >
          <PopupComponent>
            <div ref={popupRef}>{popupContent}</div>
          </PopupComponent>
        </MapPreview>
      </Box>
    </Wrapper>
  );
};
